import { useMemo, useState } from "react";
import { MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GovIssue, GovItemDetail, GovSettingsOn } from "../../../../utils/icGovernance";
import { useViewport } from "../../../../utils/useViewport";
import { NeedsReplyBadge, TICKET_FILTERS, TicketFilterKey } from "../../Support/ticketStatus";
import { useGovernance } from "../GovernanceLayout";
import {
  ENVIRONMENT_LABEL,
  StatusChip,
  clientLabel,
  dateOnly,
  itemUrl,
  severityLabel,
  statusLabel,
  statusesFor,
  vendorLabel,
} from "../governanceStatus";
import NewIssueDialog from "../dialogs/NewIssueDialog";
import { NewButton } from "../dialogs/dialogParts";
import { IdLink, LongText, PartyChip, PlainText, TitleLink, localDay } from "./cells";
import RegisterTable, { RegisterColumn, WaitingNotice } from "./RegisterTable";
import { RegisterQuery, useRegisterItems } from "./useRegisterItems";

/**
 * Issue and Bug Log — also the admin's support inbox: Support's quick filters
 * (All / Open / Needs your reply / Resolved / Closed, the same status mapping
 * as `Support/ticketStatus.TICKET_FILTERS`) over the register's `?status=`,
 * plus "Raised by me" (`?mine=1`, the server's `createdByMe`). "Needs your
 * reply" and "Raised by me" narrow the loaded rows; "Load more" keeps going.
 */

/** Support's "needs your reply": the team asked the client a question. */
const needsAnswer = (r: GovIssue) => r.status === "WAITING" && r.waitingReason === "CLARIFICATION";

// Issue and Bug Log — columns in the client's workbook order.
const issueColumns = (s: GovSettingsOn): RegisterColumn<GovIssue>[] => [
  { key: "id", header: "Issue ID", render: (r) => <IdLink item={r} /> },
  { key: "reportedAt", header: "Date Reported", render: (r) => localDay(r.reportedAt || r.createdAt) },
  { key: "reportedBy", header: "Reported By", render: (r) => <PlainText value={r.createdBy} /> },
  {
    key: "environment",
    header: "Environment",
    render: (r) => <PlainText value={r.environment ? ENVIRONMENT_LABEL[r.environment] ?? r.environment : null} />,
  },
  { key: "severity", header: "Severity", render: (r) => <PlainText value={r.severity ? severityLabel(r.severity) : null} /> },
  { key: "title", header: "Issue Summary", wrap: true, minWidth: 240, render: (r) => <TitleLink item={r} /> },
  {
    key: "steps",
    header: "Steps to Reproduce",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.stepsToReproduce} />,
  },
  {
    key: "expected",
    header: "Expected Result",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.expectedResult} />,
  },
  {
    key: "actual",
    header: "Actual Result",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.actualResult} />,
  },
  { key: "impact", header: "Business Impact", wrap: true, render: (r) => <LongText value={r.businessImpact} /> },
  { key: "leadParty", header: "Lead Party", render: (r) => <PartyChip party={r.leadParty} settings={s} /> },
  { key: "owner", header: `${vendorLabel(s)} Owner`, render: (r) => <PlainText value={r.vendorOwner || r.assignedTo} /> },
  { key: "dueDate", header: "Date Due", render: (r) => dateOnly(r.dueDate) },
  {
    key: "status",
    header: "Status",
    render: (r) => (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <StatusChip kind="ISSUE" status={r.status} />
        {needsAnswer(r) ? <NeedsReplyBadge /> : null}
      </Stack>
    ),
  },
  { key: "resolution", header: "Fix / Resolution", wrap: true, render: (r) => <LongText value={r.resolution} /> },
  { key: "retestBy", header: "Retest By", render: (r) => <PlainText value={r.retest?.by} /> },
  {
    key: "retestResult",
    header: "Retest Result",
    render: (r) => <PlainText value={r.retest?.result ? statusLabel(r.retest.result) : null} />,
  },
  { key: "closedAt", header: `${clientLabel(s)} Closure Date`, render: (r) => localDay(r.closedAt) },
];

const WAITING: WaitingNotice<GovIssue> = {
  reason: (r) => (needsAnswer(r) ? "answer" : r.status === "RETEST" ? "retest" : null),
  describe: (n, reason) =>
    `${n} ${n === 1 ? "issue is" : "issues are"} ${
      reason === "answer" ? "waiting on your answer" : "ready for you to retest and close"
    }`,
};

// ---------------------------------------------------------------------------
// Quick filters (Support's set) + "Raised by me"
// ---------------------------------------------------------------------------

const ISSUE_STATUSES = statusesFor("ISSUE");

const sameStatuses = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((s) => b.includes(s));

/** The Support filter the URL matches, or null for a hand-picked status set. */
const presetFor = (query: RegisterQuery): TicketFilterKey | null =>
  TICKET_FILTERS.find((f) => sameStatuses(f.statuses, query.status) && !!f.needsReplyOnly === query.replyOnly)
    ?.key ?? null;

type QuickFiltersProps = {
  query: RegisterQuery;
  onQueryChange: (patch: Partial<RegisterQuery>) => void;
  compact: boolean;
};

const IssueQuickFilters = ({ query, onQueryChange, compact }: QuickFiltersProps) => {
  const preset = presetFor(query);
  const choose = (key: string | null) => {
    const f = TICKET_FILTERS.find((t) => t.key === key);
    if (!f) return;
    // Kind order keeps the URL stable.
    const wanted: string[] = f.statuses;
    onQueryChange({ status: ISSUE_STATUSES.filter((s) => wanted.includes(s)), replyOnly: !!f.needsReplyOnly });
  };
  const buttonSx = { textTransform: "none", whiteSpace: "nowrap", px: 1.5 } as const;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: "wrap", mb: 1.5 }}>
      {compact ? (
        <TextField
          select
          size="small"
          label="Show"
          value={preset ?? "custom"}
          onChange={(e) => choose(e.target.value)}
          sx={{ minWidth: 200, backgroundColor: "white" }}
        >
          {TICKET_FILTERS.map((f) => (
            <MenuItem key={f.key} value={f.key}>
              {f.label}
            </MenuItem>
          ))}
          {preset ? null : (
            <MenuItem value="custom" disabled>
              Chosen statuses
            </MenuItem>
          )}
        </TextField>
      ) : (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={preset}
          onChange={(_, key) => choose(key)}
          aria-label="Filter issues"
        >
          {TICKET_FILTERS.map((f) => (
            <ToggleButton key={f.key} value={f.key} sx={buttonSx}>
              {f.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      <ToggleButton
        size="small"
        value="mine"
        selected={query.mine}
        onChange={() => onQueryChange({ mine: !query.mine })}
        sx={buttonSx}
      >
        Raised by me
      </ToggleButton>
    </Stack>
  );
};

const IssueRegister = () => {
  const { settings } = useGovernance();
  const navigate = useNavigate();
  const { isPhone } = useViewport();
  const register = useRegisterItems<GovIssue>("ISSUE");
  const columns = useMemo(() => issueColumns(settings), [settings]);
  const { mine, replyOnly } = register.query;
  const rows = useMemo(
    () => register.rows.filter((r) => (!mine || r.createdByMe) && (!replyOnly || needsAnswer(r))),
    [register.rows, mine, replyOnly],
  );
  const [newOpen, setNewOpen] = useState(false);

  const onCreated = (item: GovItemDetail) => {
    setNewOpen(false);
    navigate(itemUrl(item));
  };

  return (
    <>
      <RegisterTable
        {...register}
        rows={rows}
        clientFiltered={mine || replyOnly}
        quickFilters={
          <IssueQuickFilters query={register.query} onQueryChange={register.onQueryChange} compact={isPhone} />
        }
        kind="ISSUE"
        columns={columns}
        noun={{ one: "issue", many: "issues" }}
        caption={`${vendorLabel(settings)} fixes; ${clientLabel(settings)} retests and closes`}
        actions={<NewButton label="New issue" onClick={() => setNewOpen(true)} />}
        waiting={WAITING}
      />
      <NewIssueDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={onCreated} />
    </>
  );
};

export default IssueRegister;
